import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FkIcon, type FkIconName } from "./Icons";
import { getToken } from "../api";
import {
  readJson,
  safeStorage,
  storageKey,
  userKeyFromToken,
  writeJson,
} from "./onboarding/onboardingStorage";

export interface NavItem {
  to: string;
  label: string;
  icon?: FkIconName;
  badge?: number;
  /** Точное совпадение пути. */
  end?: boolean;
  /** Префиксы, при которых пункт активен (например, /campaigns для редактора). */
  matchPrefixes?: string[];
  /** Дополнительный регэксп активности (результат кампании). */
  matchPattern?: RegExp;
  /** Префиксы-исключения (результаты подсвечивают другой пункт). */
  excludePrefixes?: string[];
  excludePattern?: RegExp;
}

export interface NavGroup {
  key:
    | "campaigns"
    | "finance"
    | "settings"
    | "content"
    | "analytics"
    | "data"
    | "workspace";
  label: string;
  icon: FkIconName;
  items: NavItem[];
  defaultOpen?: boolean;
}

export function navItemActive(item: NavItem, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (
    item.excludePrefixes?.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
  )
    return false;
  if (item.excludePattern?.test(path)) return false;
  if (path === item.to) return true;
  if (item.end) return false;
  if (item.matchPattern?.test(path)) return true;
  return !!item.matchPrefixes?.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Сайдбар: бренд, группы меню (сворачиваются, состояние в localStorage), подвал с обучением и выходом. */
export function Sidebar({
  groups,
  roleLabel,
  onLogout,
  onStartTour,
  tourDone,
  onNavigate,
  focusFirst,
  compact,
  onToggleCompact,
}: {
  compact?: boolean;
  onToggleCompact?: () => void;
  groups: NavGroup[];
  roleLabel: string;
  onLogout: () => void;
  onStartTour: () => void;
  tourDone: boolean;
  /** Клик по пункту (закрыть мобильное меню). */
  onNavigate?: () => void;
  /** После открытия мобильного меню — фокус на первый пункт. */
  focusFirst?: boolean;
}) {
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  // Ключ меню — по пользователю (общий компьютер: настройка одного не применяется ко всем).
  const navKey = useRef("fk_sms_nav:anon");
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      groups.map((group) => [group.key, group.defaultOpen !== false]),
    ),
  );

  useEffect(() => {
    // Новая версия раскрывает старое свёрнутое меню один раз; последующие
    // ручные изменения сохраняются отдельно для каждого пользователя.
    navKey.current = storageKey("nav", userKeyFromToken(getToken())) + ":v2";
    const saved = readJson<Record<string, boolean>>(
      safeStorage(),
      navKey.current,
    );
    if (saved && typeof saved === "object")
      setOpen((current) => ({ ...current, ...saved }));
  }, []);

  useEffect(() => {
    if (focusFirst)
      navRef.current?.querySelector<HTMLElement>("a, button")?.focus();
  }, [focusFirst]);

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = { ...current, [key]: !current[key] };
      writeJson(safeStorage(), navKey.current, next);
      return next;
    });

  return (
    <aside id="fk-sidebar" className="fk-sidebar" aria-label="Боковое меню">
      <Link
        to="/campaign-dashboard"
        className="fk-brand"
        aria-label="FISHKA Marketing — обзор"
      >
        <img
          className="fk-brand-mark"
          src="/assets/logo/fishka-mark-sky.svg"
          alt=""
        />
        <img
          className="fk-brand-wordmark"
          src="/assets/logo/fishka-wordmark-indigo.svg"
          alt="FISHKA Marketing"
        />
      </Link>
      <nav ref={navRef} aria-label="Главное меню">
        {groups.map((group) => {
          const expanded = open[group.key] !== false;
          return (
            <div className="fk-nav-group" key={group.key}>
              <button
                type="button"
                className="fk-nav-group-btn"
                aria-expanded={expanded}
                aria-controls={`fk-nav-${group.key}`}
                onClick={() => toggle(group.key)}
              >
                <FkIcon name={group.icon} size={18} />
                <span>{group.label}</span>
                <FkIcon name={expanded ? "chevD" : "chevR"} size={14} />
              </button>
              {(expanded || compact) && (
                <div id={`fk-nav-${group.key}`}>
                  {group.items.map((item) => {
                    const active = navItemActive(item, pathname);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        title={item.label}
                        aria-label={item.label}
                        className={`fk-nav-item${active ? " active" : ""}`}
                        aria-current={active ? "page" : undefined}
                        onClick={onNavigate}
                      >
                        <FkIcon name={item.icon || group.icon} size={18} />
                        <span>{item.label}</span>
                        {!!item.badge && (
                          <span className="fk-nav-badge">{item.badge}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="fk-sidebar-foot">
        <button
          type="button"
          className="studio-menu-toggle"
          onClick={onToggleCompact}
          aria-label={compact ? "Развернуть меню" : "Свернуть меню"}
          title={compact ? "Развернуть меню" : "Свернуть меню"}
        >
          <FkIcon name={compact ? "chevR" : "back"} size={16} />
          <span>Свернуть меню</span>
        </button>
        <button
          type="button"
          className="fk-tour-btn"
          title="Обучение"
          aria-label="Обучение"
          onClick={onStartTour}
        >
          <FkIcon name="bulb" size={18} />
          <span>{tourDone ? "Пройти обучение заново" : "Пройти обучение"}</span>
        </button>
        <div className="fk-sidebar-foot-row">
          <span>{roleLabel}</span>
          <button type="button" className="fk-logout" onClick={onLogout}>
            <FkIcon name="out" size={16} /> Выйти
          </button>
        </div>
      </div>
    </aside>
  );
}
